/**
 * Página del Portal de Transparencia - Estilo Awwwards Interior
 * Información corporativa y cumplimiento normativo de INTERCASA, S.A.
 * Diseño elegante y funcional con animaciones sutiles
 */

import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import { AnimatedSection } from '../components/animations';

gsap.registerPlugin(ScrollTrigger);

/**
 * Página del Portal de Transparencia con estilo Awwwards
 * @returns {JSX.Element} Página del Portal de Transparencia inmersiva
 */
const TransparencyPage = () => {
  const heroRef = useRef(null);
  const cardsRef = useRef(null);

  // Animación del hero al cargar
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    
    // Animación de entrada del hero
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 40, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    )
    .fromTo(hero.querySelector('.hero-subtitle'),
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' },
      '-=0.4'
    );

    return () => tl.kill();
  }, []);

  // Animaciones para las tarjetas de transparencia - por filas
  useEffect(() => {
    const cardsContainer = cardsRef.current;
    if (!cardsContainer) return;

    const cards = cardsContainer.querySelectorAll('.transparency-card');
    
    // Animar todas las cards con stagger, coordinado con el scroll
    gsap.fromTo(cards,
      { y: 40, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.5,
        stagger: {
          amount: 0.4, // Tiempo total para todas las cards
          from: 'start',
          grid: [3, 3], // Grid de 3 columnas
          axis: 'y', // Animar por filas
        },
        ease: 'power2.out',
        scrollTrigger: {
          trigger: cardsContainer,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <ImmersiveLayout>
      {/* 
        =============================================
        1. HERO CON BANNER - Imagen de fondo
        =============================================
      */}
      <section 
        ref={heroRef}
        className="relative min-h-[60vh] flex items-center overflow-hidden"
      >
        {/* Fondo negro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen de fondo */}
        <div 
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage: 'url(/images/banners/transparencia-banner.webp)',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />

        {/* Contenido del hero */}
        <div className="relative z-10 container mx-auto px-4 md:px-8 py-20">
          <div className="max-w-4xl mx-auto text-center">
            {/* Etiqueta superior */}
            <AnimatedSection animation="fadeUp" delay={0}>
              <span className="inline-block font-lato text-intercasa-yellow text-sm uppercase tracking-[0.2em] mb-6">
                Transparencia corporativa
              </span>
            </AnimatedSection>

            {/* Título */}
            <h1 className="hero-title font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[1.1] mb-6">
              Portal de{' '}
              <span className="text-intercasa-yellow">Transparencia</span>
            </h1>

            {/* Subtítulo */}
            <p className="hero-subtitle font-lato text-white/90 text-lg md:text-xl lg:text-2xl leading-relaxed max-w-3xl mx-auto text-center">
              Información corporativa y cumplimiento normativo de INTERCASA, S.A.
            </p>
          </div>
        </div>
      </section>
      {/* 
        =============================================
        2. INTRODUCCIÓN - Contexto legal
        =============================================
      */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection animation="fadeUp">
              <p className="font-lato text-lg text-intercasa-dark/70 leading-relaxed mb-6">
                Con este Portal de Transparencia, <strong className="text-intercasa-dark">INTERCASA, S.A.</strong> pretende facilitar al usuario el acceso a la información de esta Sociedad Mercantil, de una forma fácil y sencilla, en atención a lo dispuesto en la Ley 19/2013, de 9 de diciembre, de transparencia, acceso a la información pública y buen gobierno, así como la Ley 12/2014, de 26 de diciembre, de transparencia y de acceso a la información pública de Canarias.
              </p>
            </AnimatedSection>
            
            <AnimatedSection animation="fadeUp" delay={0.1}>
              <p className="font-lato text-base text-intercasa-dark/70 leading-relaxed mb-6">
                La Ley Canaria 12/2014 expresa en su artículo 3.1, letra b) que las empresas o entidades privadas que reciben más de sesenta mil (60.000 €) euros anuales de ayudas públicas tienen un conjunto de obligaciones informativas.
              </p>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.15}>
              <p className="font-lato text-base text-intercasa-dark/70 leading-relaxed mb-6">
                En el mismo sentido, la Ley 19/2013, dispone en su artículo 3, letra b), que estarán igualmente obligadas las entidades privadas que perciban, en el periodo de un año, ayudas o subvenciones públicas por cuantía superior a cien mil euros (100.000 €), o cuando al menos el cuarenta por ciento (40 %) del total de sus ingresos anuales tenga carácter de ayuda o subvención pública, siempre que alcancen como mínimo la cantidad de cinco mil euros (5.000 €).
              </p>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.2}>
              <p className="font-lato text-base text-intercasa-dark/70 leading-relaxed mb-6">
                Además, en el artículo 64 de la Ley de Transparencia de Canarias, se especifica que las entidades privadas "deberán facilitarle al comisionado o comisionada de Transparencia y Acceso a la Información Pública la información que les solicite y prestarle la colaboración necesaria para el desarrollo de sus funciones. Específicamente, deberán mantener actualizada y disponible información detallada sobre el grado de aplicación de la ley en sus respectivos ámbitos competenciales".
              </p>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.25}>
              <p className="font-lato text-base text-intercasa-dark/70 leading-relaxed mb-8">
                Por este motivo, <strong className="text-intercasa-dark">INTERCASA, S.A.</strong> se compromete a poner a disposición de las administraciones y el público general, dentro de lo previsto por las normas citadas, toda la información relativa a la entidad, especialmente aquella relacionada a las ayudas y subvenciones públicas obtenidas, en cumplimiento de la normativa antes mencionada.
              </p>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.3}>
              <p className="font-lato text-base text-intercasa-dark/70 leading-relaxed mb-8">
                Igualmente, <strong className="text-intercasa-dark">INTERCASA, S.A</strong> mantendrá este portal actualizado para que nuestros clientes, proveedores y la ciudadanía pueda consultar nuestra actividad, según la Ley de Transparencia.
              </p>
            </AnimatedSection>
            
            <AnimatedSection animation="fadeUp" delay={0.35}>
              <div className="bg-intercasa-bone/50 border-l-4 border-intercasa-yellow p-6 rounded-r-lg mb-8">
                <p className="font-semibold text-intercasa-dark mb-3">Compromiso voluntario con la transparencia:</p>
                <p className="text-intercasa-dark/70 text-base leading-relaxed">
                  Aunque <strong className="text-intercasa-dark">INTERCASA, S.A.</strong> no se encuentra legalmente obligada a cumplir con los requisitos establecidos en la Ley 19/2013, de 9 de diciembre, de Transparencia, Acceso a la Información Pública y Buen Gobierno, la entidad adopta voluntariamente los principios de transparencia y acceso a la información en el ejercicio de su actividad. Esta decisión responde al compromiso adquirido en el marco de la subvención pública para el fomento de la descarbonización del sector industrial, en el marco de la Estrategia de Energía Sostenible en las Islas Canarias (Programa 3, Líneas 1 y 2), con cargo al Plan de Recuperación, Transformación y Resiliencia (Componente 7, Inversión 2).
                </p>
              </div>
            </AnimatedSection>

            {/* Botones de descarga */}
            <AnimatedSection animation="fadeUp" delay={0.4}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6 border-t border-intercasa-dark/10">
                <p className="font-lato text-intercasa-dark font-semibold text-sm mb-2 sm:mb-0">
                  Descargar introducción:
                </p>
                <div className="flex gap-4">
                  {/* Botón PDF */}
                  <a
                    href="/PortalTansparenciaIntercasa/IntroduccionIntercasa/IntroduccionIntercasa.pdf"
                    download
                    className="group inline-flex items-center gap-2 px-6 py-3 bg-intercasa-red hover:bg-intercasa-red/90 text-white rounded-lg transition-all duration-300 hover:shadow-lg"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                    </svg>
                    <span className="font-lato font-medium text-sm">PDF</span>
                  </a>

                  {/* Botón ODT */}
                  <a
                    href="/PortalTansparenciaIntercasa/IntroduccionIntercasa/IntroduccionIntercasa.odt"
                    download
                    className="group inline-flex items-center gap-2 px-6 py-3 bg-intercasa-dark hover:bg-intercasa-dark/90 text-white rounded-lg transition-all duration-300 hover:shadow-lg"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                    </svg>
                    <span className="font-lato font-medium text-sm">ODT</span>
                  </a>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        3. GRID DE SECCIONES DE TRANSPARENCIA
        =============================================
      */}
      <section className="bg-intercasa-bone py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          {/* Header de sección */}
          <AnimatedSection animation="fadeUp" className="mb-16">
            <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
              Información Pública
            </span>
            <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl lg:text-5xl">
              Secciones del Portal
            </h2>
          </AnimatedSection>

          {/* Grid de tarjetas */}
          <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Información en Materia Organizativa */}
            <Link
              to="/transparencia/normativa"
              className="transparency-card group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 p-6 flex flex-col"
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-intercasa-red/10 rounded-xl flex items-center justify-center group-hover:bg-intercasa-red group-hover:scale-110 transition-all duration-300">
                  <svg className="w-8 h-8 text-intercasa-red group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-4 0H9m-4 0H5m0 0v-5a2 2 0 012-2h2a2 2 0 012 2v5" />
                  </svg>
                </div>
              </div>
              <div className="flex-grow flex flex-col">
                <h3 className="font-baskerville text-xl font-bold text-intercasa-dark mb-2 text-center group-hover:text-intercasa-red transition-colors duration-300 min-h-[56px] flex items-center justify-center">
                  Información en Materia Organizativa
                </h3>
                <p className="text-sm text-intercasa-gray mb-4 text-center">
                  Actualizado noviembre 2025
                </p>
              </div>
              <span className="flex items-center justify-center text-intercasa-red font-semibold text-sm group-hover:gap-3 gap-2 transition-all duration-300 mt-auto">
                Ver más detalles
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
              {/* Línea inferior animada */}
              <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            </Link>

            {/* Información de Administradores y Directivos */}
            <Link
              to="/transparencia/organizativa"
              className="transparency-card group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 p-6 flex flex-col"
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-intercasa-red/10 rounded-xl flex items-center justify-center group-hover:bg-intercasa-red group-hover:scale-110 transition-all duration-300">
                  <svg className="w-8 h-8 text-intercasa-red group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {/* Icono de persona con corbata/ejecutivo */}
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14c-4 0-6 2-6 4v1h12v-1c0-2-2-4-6-4z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l-1 3h2l-1-3z" />
                  </svg>
                </div>
              </div>
              <div className="flex-grow flex flex-col">
                <h3 className="font-baskerville text-xl font-bold text-intercasa-dark mb-2 text-center group-hover:text-intercasa-red transition-colors duration-300 min-h-[56px] flex items-center justify-center">
                  Información de Administradores y Directivos
                </h3>
                <p className="text-sm text-intercasa-gray mb-4 text-center">
                  Actualizado noviembre 2025
                </p>
              </div>
              <span className="flex items-center justify-center text-intercasa-red font-semibold text-sm group-hover:gap-3 gap-2 transition-all duration-300 mt-auto">
                Ver más detalles
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            </Link>

            {/* Servicios y Procedimientos */}
            <Link
              to="/transparencia/servicios"
              className="transparency-card group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 p-6 flex flex-col"
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-intercasa-red/10 rounded-xl flex items-center justify-center group-hover:bg-intercasa-red group-hover:scale-110 transition-all duration-300">
                  <svg className="w-8 h-8 text-intercasa-red group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
              </div>
              <div className="flex-grow flex flex-col">
                <h3 className="font-baskerville text-xl font-bold text-intercasa-dark mb-2 text-center group-hover:text-intercasa-red transition-colors duration-300 min-h-[56px] flex items-center justify-center">
                  Servicios y Procedimientos
                </h3>
                <p className="text-sm text-intercasa-gray mb-4 text-center">
                  Actualizado noviembre 2025
                </p>
              </div>
              <span className="flex items-center justify-center text-intercasa-red font-semibold text-sm group-hover:gap-3 gap-2 transition-all duration-300 mt-auto">
                Ver más detalles
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            </Link>

            {/* Información Económico-Financiera */}
            <Link
              to="/transparencia/economica"
              className="transparency-card group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 p-6 flex flex-col"
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-intercasa-red/10 rounded-xl flex items-center justify-center group-hover:bg-intercasa-red group-hover:scale-110 transition-all duration-300">
                  <svg className="w-8 h-8 text-intercasa-red group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
              <div className="flex-grow flex flex-col">
                <h3 className="font-baskerville text-xl font-bold text-intercasa-dark mb-2 text-center group-hover:text-intercasa-red transition-colors duration-300 min-h-[56px] flex items-center justify-center">
                  Información Económico-Financiera
                </h3>
                <p className="text-sm text-intercasa-gray mb-4 text-center">
                  Actualizado noviembre 2025
                </p>
              </div>
              <span className="flex items-center justify-center text-intercasa-red font-semibold text-sm group-hover:gap-3 gap-2 transition-all duration-300 mt-auto">
                Ver más detalles
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            </Link>

            {/* Información de Contratos */}
            <Link
              to="/transparencia/acceso"
              className="transparency-card group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 p-6 flex flex-col"
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-intercasa-red/10 rounded-xl flex items-center justify-center group-hover:bg-intercasa-red group-hover:scale-110 transition-all duration-300">
                  <svg className="w-8 h-8 text-intercasa-red group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="flex-grow flex flex-col">
                <h3 className="font-baskerville text-xl font-bold text-intercasa-dark mb-2 text-center group-hover:text-intercasa-red transition-colors duration-300 min-h-[56px] flex items-center justify-center">
                  Información de Contratos
                </h3>
                <p className="text-sm text-intercasa-gray mb-4 text-center">
                  Actualizado noviembre 2025
                </p>
              </div>
              <span className="flex items-center justify-center text-intercasa-red font-semibold text-sm group-hover:gap-3 gap-2 transition-all duration-300 mt-auto">
                Ver más detalles
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            </Link>

            {/* Contratos y Convenios */}
            <Link
              to="/transparencia/contratos"
              className="transparency-card group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 p-6 flex flex-col"
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-intercasa-red/10 rounded-xl flex items-center justify-center group-hover:bg-intercasa-red group-hover:scale-110 transition-all duration-300">
                  <svg className="w-8 h-8 text-intercasa-red group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
              <div className="flex-grow flex flex-col">
                <h3 className="font-baskerville text-xl font-bold text-intercasa-dark mb-2 text-center group-hover:text-intercasa-red transition-colors duration-300 min-h-[56px] flex items-center justify-center">
                  Convenios y Encomiendas de Gestión
                </h3>
                <p className="text-sm text-intercasa-gray mb-4 text-center">
                  Actualizado noviembre 2025
                </p>
              </div>
              <span className="flex items-center justify-center text-intercasa-red font-semibold text-sm group-hover:gap-3 gap-2 transition-all duration-300 mt-auto">
                Ver más detalles
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            </Link>

            {/* Subvenciones y Ayudas Públicas */}
            <Link
              to="/transparencia/ayudas"
              className="transparency-card group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 p-6 flex flex-col"
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-intercasa-red/10 rounded-xl flex items-center justify-center group-hover:bg-intercasa-red group-hover:scale-110 transition-all duration-300">
                  <svg className="w-8 h-8 text-intercasa-red group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="flex-grow flex flex-col">
                <h3 className="font-baskerville text-xl font-bold text-intercasa-dark mb-2 text-center group-hover:text-intercasa-red transition-colors duration-300 min-h-[56px] flex items-center justify-center">
                  Subvenciones y Ayudas Públicas
                </h3>
                <p className="text-sm text-intercasa-gray mb-4 text-center">
                  Actualizado noviembre 2025
                </p>
              </div>
              <span className="flex items-center justify-center text-intercasa-red font-semibold text-sm group-hover:gap-3 gap-2 transition-all duration-300 mt-auto">
                Ver más detalles
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            </Link>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        4. CTA FINAL - CONTACTO
        =============================================
      */}
      <section className="bg-intercasa-dark py-24 md:py-32 relative overflow-hidden">
        {/* Elementos decorativos */}
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-intercasa-red/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-intercasa-red/10 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection animation="fadeUp">
              <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-6 block">
                Más Información
              </span>
              
              <h2 className="font-baskerville font-bold text-white text-3xl md:text-4xl lg:text-5xl mb-8 leading-tight">
                Solicitar Información<br />
                <span className="text-intercasa-yellow">Adicional</span>
              </h2>
              
              <p className="font-lato text-lg text-white/60 leading-relaxed mb-12">
                Para acceder a información detallada o solicitar documentación adicional del Portal de Transparencia, puede contactar con nosotros.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="mailto:notificaciones@grumasa.es"
                  className="group inline-flex items-center justify-center bg-white text-intercasa-dark font-semibold px-8 py-4 rounded-full hover:bg-intercasa-yellow transition-all duration-300"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Solicitar información
                  <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
                
                <a 
                  href="https://sede.registradores.org/sede/sede-corpme-web/home"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center bg-transparent border-2 border-white/30 text-white font-semibold px-8 py-4 rounded-full hover:border-white hover:bg-white/10 transition-all duration-300"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Registro Mercantil
                  <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default TransparencyPage;